import { roundNumberToNDecimalPlaces } from '@app/utils/helpers';
import { ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { renderUnit } from '@shared/components/content/unit';
import { EmissionBarCell } from '@shared/components/data-table/cells/emission-bar-cell';
import {
  getBandRange,
  ratingSections,
} from '@shared/components/new-ratings-threshold-slider';
import { cn, formatGrams, formatSmallNumber } from '@shared/lib/utils';
import { buildBreakdownPages } from './build-pdf-breakdown-pages';
import {
  buildPdfIngredientRows,
  PdfIngredientRow,
} from './build-pdf-ingredient-rows';
import { ProductPdfPage } from './product-pdf-page';
import { buildPptSections, PptSectionTable } from './product-pdf-ppt-sections';

const INDENT_PX = 16;

const GRID_COLS = 'grid-cols-[minmax(0,1fr)_64px_112px_88px_150px]';

interface ProductPdfBreakdownPagesProps {
  productResult: ProductResult;
  organizationName: string;
}

/**
 * The full breakdown: the ingredient table (with sub-products expanded),
 * followed by the processing, packaging and transport tables, flowed across
 * as many A4 pages as needed.
 */
export function ProductPdfBreakdownPages({
  productResult,
  organizationName,
}: ProductPdfBreakdownPagesProps) {
  const rows = buildPdfIngredientRows(productResult.ingredients);
  const sections = buildPptSections(productResult);
  const pages = buildBreakdownPages(rows, sections);

  // One scale for every table, as on the product page, so bars compare
  // across ingredients, processing, packaging and transport.
  const maxEmissions =
    [
      ...rows.filter((row) => row.depth === 0),
      ...sections.flatMap((s) => s.rows),
    ].reduce((max, row) => Math.max(max, row.emissions), 0) * 1.1;

  return (
    <>
      {pages.map((page, pageIndex) => {
        const isLastPage = pageIndex === pages.length - 1;
        const hasIngredients = page.ingredientRows.length > 0;
        const caption = hasIngredients
          ? 'Emissions by ingredient (continued)'
          : 'Processing, packaging and transport';
        return (
          <ProductPdfPage key={pageIndex} last={isLastPage}>
            <div className="flex flex-1 flex-col gap-3 px-6 pb-6 pt-8">
              {pageIndex === 0 ? (
                <IngredientsPageIntro
                  productResult={productResult}
                  organizationName={organizationName}
                />
              ) : (
                <p className="truncate text-xs text-gray-500">
                  <span className="font-bold text-primary-800">
                    {productResult.name}
                  </span>{' '}
                  · {caption}
                </p>
              )}

              {pageIndex === 0 && <RatingLegend />}
              {hasIngredients && (
                <IngredientsTable
                  rows={page.ingredientRows}
                  maxEmissions={maxEmissions}
                />
              )}
              {page.showTotal && <IngredientsTotalRow rows={rows} />}

              {page.sections.map((section) => (
                <div key={section.stage} className="pt-2">
                  <PptSectionTable
                    section={section}
                    maxEmissions={maxEmissions}
                  />
                </div>
              ))}
            </div>
          </ProductPdfPage>
        );
      })}
    </>
  );
}

function IngredientsPageIntro({
  productResult,
  organizationName,
}: {
  productResult: ProductResult;
  organizationName: string;
}) {
  const {
    name,
    netWeight,
    totalIngredientWeight,
    servings,
    ingredientShareOfTotalEmissions,
  } = productResult;

  // Net weight when the product records one, labelled so it isn't mistaken
  // for page 1's product weight (total ingredient weight). Both are kg and
  // rounded as on page 1.
  const weight =
    netWeight != null
      ? { label: 'Net weight', kg: netWeight }
      : { label: 'Product weight', kg: totalIngredientWeight ?? 0 };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-[11px] font-bold uppercase text-primary-600">
          {organizationName}
        </p>
        <h1 className="truncate text-2xl font-bold text-primary-800">{name}</h1>
        <p className="font-heading text-md font-bold text-primary-700">
          Emissions by ingredient
        </p>
      </div>

      <div className="flex items-center gap-6 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <Icons.scale size={14} />
          {weight.label} {roundNumberToNDecimalPlaces(weight.kg, 2)} kg
        </span>
        <span className="flex items-center gap-1">
          <Icons.pizza size={14} />
          {servings} serving{servings > 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1 font-medium text-teal-600">
          <Icons.pieChart size={14} />
          {ingredientShareOfTotalEmissions
            ? `${(ingredientShareOfTotalEmissions * 100).toFixed(1)}%`
            : '0%'}{' '}
          of total emissions
        </span>
      </div>
    </div>
  );
}

// Units sit under the bold column names in lighter, smaller type.
const UNIT_CLASS = 'block text-[10px] font-normal leading-3 text-gray-500';

function IngredientsTable({
  rows,
  maxEmissions,
}: {
  rows: PdfIngredientRow[];
  maxEmissions: number;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-gray-200 text-xs">
      <div
        className={cn(
          'grid items-end border-b border-gray-200 bg-gray-50 px-3 py-2 font-semibold text-gray-700',
          GRID_COLS
        )}
      >
        <span>Ingredient</span>
        <span className="text-right">
          Weight
          <span className={UNIT_CLASS}>g</span>
        </span>
        <span className="text-right">
          Intensity
          <span className={UNIT_CLASS}>{renderUnit('kgCO2e/kg')}</span>
        </span>
        <span className="pr-4 text-right">
          Emissions
          <span className={UNIT_CLASS}>{renderUnit('kgCO2e')}</span>
        </span>
        <span />
      </div>

      {rows.map((row) => (
        <IngredientRow key={row.id} row={row} maxEmissions={maxEmissions} />
      ))}
    </div>
  );
}

function IngredientRow({
  row,
  maxEmissions,
}: {
  row: PdfIngredientRow;
  maxEmissions: number;
}) {
  const isNested = row.depth > 0;
  const isPpt = row.kind !== 'ingredient';

  return (
    <div
      className={cn(
        'grid h-7 items-center border-b border-gray-100 px-3 last:border-b-0',
        GRID_COLS,
        isNested ? 'text-gray-600' : 'text-gray-900',
        row.hasChildren && 'font-medium'
      )}
    >
      <span
        className={cn('truncate pr-3', isPpt && 'italic')}
        // Indent depends on nesting depth, so it can't be a static class.
        style={{ paddingLeft: row.depth * INDENT_PX }}
      >
        {isNested && (
          <span className="mr-1.5 inline-block w-2 border-t border-gray-300 align-middle" />
        )}
        {row.name}
      </span>
      <span className="text-right">
        {row.weight == null ? '–' : formatGrams(row.weight)}
      </span>
      <span className="text-right">
        {row.carbonIntensity == null
          ? '–'
          : formatSmallNumber(row.carbonIntensity)}
      </span>
      <span className="pr-4 text-right">
        {formatSmallNumber(row.emissions)}
      </span>
      <div className="h-full">
        <EmissionBarCell
          type={row.kind}
          emissions={row.emissions}
          maxEmissions={maxEmissions}
          emissionsCategory={row.emissionsCategory ?? undefined}
          showHover={false}
          className="w-full pr-2"
        />
      </div>
    </div>
  );
}

function RatingLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] leading-4 text-gray-500">
      <span>
        Bar colour shows the carbon rating of the ingredient&apos;s intensity (
        {renderUnit('kgCO2e/kg')}):
      </span>
      {ratingSections.map(({ rating, bgColor }, index) => (
        <span key={rating} className="flex items-center gap-1">
          <span
            // Same 70% opacity as the bars, so the swatches match them exactly.
            className="size-2 rounded-full opacity-70"
            style={{ backgroundColor: bgColor }}
          />
          <span className="font-semibold text-gray-700">{rating}</span>
          <span>{getBandRange(index)}</span>
        </span>
      ))}
    </div>
  );
}

function IngredientsTotalRow({ rows }: { rows: PdfIngredientRow[] }) {
  // Sum the top-level rows so the total always agrees with the table above.
  const topLevel = rows.filter((row) => row.depth === 0);
  const totalWeight = topLevel.reduce((sum, row) => sum + (row.weight ?? 0), 0);
  const totalEmissions = topLevel.reduce((sum, row) => sum + row.emissions, 0);

  return (
    <div
      className={cn(
        'grid h-8 items-center rounded-md bg-teal-50 px-3 text-xs font-bold text-primary-800',
        GRID_COLS
      )}
    >
      <span>Total</span>
      <span className="text-right">{formatGrams(totalWeight)}</span>
      <span />
      <span className="pr-4 text-right">
        {formatSmallNumber(totalEmissions)}
      </span>
      <span />
    </div>
  );
}
